/**
 * lib/useFleet.ts — the live car-hire fleet.
 * Reads a single Firestore document `fleet/list` (edited from the portal's
 * Fleet page). Falls back to the static seed in data/fleet.ts when the doc is
 * missing or unreachable, so the public site always renders. Mirrors useTreatments.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { FLEET_DEFAULTS, type Vehicle } from '@/data/fleet';

export interface FleetData {
  vehicles: Vehicle[];
}

export const SEED_FLEET: FleetData = { vehicles: FLEET_DEFAULTS };

export function useFleet() {
  const [data, setData] = useState<FleetData>(SEED_FLEET);
  const [live, setLive] = useState(false);

  useEffect(() => {
    return onSnapshot(
      doc(db, 'fleet', 'list'),
      (snap) => {
        if (snap.exists()) {
          const d = snap.data() as Partial<FleetData>;
          setData({ vehicles: d.vehicles?.length ? d.vehicles : SEED_FLEET.vehicles });
          setLive(true);
        } else {
          setData(SEED_FLEET);
          setLive(false);
        }
      },
      () => {
        setData(SEED_FLEET);
        setLive(false);
      },
    );
  }, []);

  const active = data.vehicles.filter((v) => v.active !== false);
  return { vehicles: data.vehicles, active, live };
}
